import { Link, useForm } from "@inertiajs/react"

import type { SharedProps } from "@/types"
import { AuthCard } from "@/components/auth-card"
import { AuthShell } from "@/components/auth-shell"
import { AuthStatus } from "@/components/auth-status"
import { Button } from "@/components/ui/button"
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"

type SignUpPageProps = SharedProps & {
  errorSummary: string[]
  paths: {
    signIn: string
    signUp: string
  }
  values: {
    email?: string
  }
}

export default function SignUpPage({
  errorSummary,
  flash,
  paths,
  values,
}: SignUpPageProps) {
  const form = useForm({
    email: values.email ?? "",
    password: "",
    password_confirmation: "",
  })

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    form.post(paths.signUp, {
      onFinish: () => form.reset("password", "password_confirmation"),
    })
  }

  return (
    <AuthShell
      heroDescription="INNLY helps properties create personalized local guides that guests actually use — delivered the moment they connect to your WiFi."
      heroPoints={[
        "Set up your property guide in minutes, not hours.",
        "Your guides reach guests at the exact moment they need local picks.",
        "Start with a template — customize everything later.",
      ]}
      heroTitle="Let's build your guest experience."
      pageTitle="Create your account"
    >
      <div className="flex flex-col gap-4">
        <AuthStatus errorSummary={errorSummary} flash={flash} />
        <AuthCard
          description="Create an account to start using Innly."
          eyebrow="New account"
          title="Sign up"
        >
          <form onSubmit={submit}>
            <FieldGroup>
              <Field data-invalid={!!form.errors.email}>
                <FieldLabel htmlFor="email">Email</FieldLabel>
                <Input
                  aria-invalid={!!form.errors.email}
                  autoComplete="email"
                  id="email"
                  name="email"
                  onChange={(event) => form.setData("email", event.target.value)}
                  placeholder="name@example.com"
                  required
                  type="email"
                  value={form.data.email}
                />
                <FieldError>{form.errors.email}</FieldError>
              </Field>
              <Field data-invalid={!!form.errors.password}>
                <FieldLabel htmlFor="password">Password</FieldLabel>
                <Input
                  aria-invalid={!!form.errors.password}
                  autoComplete="new-password"
                  id="password"
                  name="password"
                  onChange={(event) => form.setData("password", event.target.value)}
                  required
                  type="password"
                  value={form.data.password}
                />
                <FieldDescription>Use at least 12 characters.</FieldDescription>
                <FieldError>{form.errors.password}</FieldError>
              </Field>
              <Field data-invalid={!!form.errors.password_confirmation}>
                <FieldLabel htmlFor="password_confirmation">
                  Confirm password
                </FieldLabel>
                <Input
                  aria-invalid={!!form.errors.password_confirmation}
                  autoComplete="new-password"
                  id="password_confirmation"
                  name="password_confirmation"
                  onChange={(event) =>
                    form.setData("password_confirmation", event.target.value)
                  }
                  required
                  type="password"
                  value={form.data.password_confirmation}
                />
                <FieldError>{form.errors.password_confirmation}</FieldError>
              </Field>
              <Field>
                <Button className="w-full" disabled={form.processing} type="submit">
                  {form.processing ? "Creating account..." : "Create account"}
                </Button>
                <FieldDescription className="text-center">
                  Already have an account? <Link href={paths.signIn}>Sign in</Link>
                </FieldDescription>
              </Field>
            </FieldGroup>
          </form>
        </AuthCard>
      </div>
    </AuthShell>
  )
}
